import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import { CATEGORY_TYPES, DEFAULT_FINANCIAL_CATEGORIES } from '../domain/finance';
import { createCategory, setCategoryActive, updateCategory } from '../services/categoryService';

const byName = (left, right) => left.name.localeCompare(right.name, 'pt-BR');

export const useCategories = (accountId) => {
  const [customCategories, setCustomCategories] = useState([]);
  const [loading, setLoading] = useState(Boolean(accountId));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!accountId) {
      setCustomCategories([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    setLoading(true);
    return onSnapshot(
      query(collection(firestore, 'accounts', accountId, 'categories'), orderBy('name')),
      (snapshot) => {
        setCustomCategories(snapshot.docs.map((item) => ({ id: item.id, ...item.data(), source: 'custom' })));
        setLoading(false);
        setError(null);
      },
      (nextError) => {
        setError(nextError);
        setLoading(false);
      }
    );
  }, [accountId]);

  const categories = useMemo(() => [
    ...DEFAULT_FINANCIAL_CATEGORIES,
    ...customCategories.filter((item) => item.active),
  ].sort(byName), [customCategories]);

  const categoryNames = useMemo(() => categories.map((item) => item.name), [categories]);
  const expenseCategories = useMemo(
    () => categories.filter((item) => item.type === CATEGORY_TYPES.EXPENSE || item.type === CATEGORY_TYPES.BOTH),
    [categories]
  );
  const incomeCategories = useMemo(
    () => categories.filter((item) => item.type === CATEGORY_TYPES.INCOME || item.type === CATEGORY_TYPES.BOTH),
    [categories]
  );

  const create = useCallback((input) => createCategory({ accountId, ...input }), [accountId]);
  const update = useCallback((input) => updateCategory({ accountId, ...input }), [accountId]);
  const setActive = useCallback((categoryId, active) => setCategoryActive({ accountId, categoryId, active }), [accountId]);

  return {
    categories,
    categoryNames,
    expenseCategories,
    incomeCategories,
    customCategories,
    loading,
    error,
    createCategory: create,
    updateCategory: update,
    setCategoryActive: setActive,
  };
};

export default useCategories;